import React from 'react';
import { useGetSeatsIOEvent } from 'hooks/useGetSeatsIOEvent';
import { useGetSeatsIOEventReport } from 'hooks/useGetSeatsIOEventReport';
import { useGetSeatsChart } from 'hooks/useGetSeatsChart';
import { SeatsIOCategory } from 'services/Seats';
import * as Styled from './SeatsCategories.styles';

interface SeatsCategoriesProps {
	eventKey: string | null | undefined;
	chartKey: string | null | undefined;
	selectedCategory?: string;
	onCategorySelect?: (category: SeatsIOCategory) => void;
}

export const SeatsCategories: React.FC<SeatsCategoriesProps> = ({
	eventKey,
	chartKey,
	selectedCategory,
	onCategorySelect,
}) => {
	const { data: seatsEvent, isLoading, error } = useGetSeatsIOEvent(eventKey);
	const { data: availabilityReport } = useGetSeatsIOEventReport(eventKey);
	const { data: chartDetails, isLoading: chartLoading } = useGetSeatsChart(chartKey);

	const getPlaceCount = (categoryLabel: string): number => {
		if (!availabilityReport?.available?.byCategoryLabel) return 0;
		return availabilityReport.available.byCategoryLabel[categoryLabel] || 0;
	};

	if (!eventKey && !chartKey) {
		return null;
	}

	if (isLoading || (chartKey && chartLoading)) {
		return (
			<Styled.CategoriesSection>
				<Styled.CategoriesTitle>Seat Categories</Styled.CategoriesTitle>
				<Styled.LoadingState>Loading seat categories...</Styled.LoadingState>
			</Styled.CategoriesSection>
		);
	}

	if (error) {
		return (
			<Styled.CategoriesSection>
				<Styled.CategoriesTitle>Seat Categories</Styled.CategoriesTitle>
				<Styled.ErrorState>Failed to load seat categories.</Styled.ErrorState>
			</Styled.CategoriesSection>
		);
	}

	if (!seatsEvent?.categories || seatsEvent.categories.length === 0) {
		// If we have no seat event but have chart details, show just the chart
		if (chartDetails) {
			return (
				<Styled.CategoriesSection>
					<Styled.CategoriesTitle>Seating Chart</Styled.CategoriesTitle>
					<Styled.ChartContainer style={{ maxWidth: '100%' }}>
						<Styled.ChartContent>
							<Styled.ChartTitle>{chartDetails.name}</Styled.ChartTitle>
							{chartDetails.publishedVersionThumbnailUrl && (
								<Styled.ChartThumbnail
									src={chartDetails.publishedVersionThumbnailUrl}
									alt={`${chartDetails.name} seating chart`}
								/>
							)}
							<Styled.ChartInfo>Venue: {chartDetails.venueType || 'Standard'}</Styled.ChartInfo>
						</Styled.ChartContent>
					</Styled.ChartContainer>
				</Styled.CategoriesSection>
			);
		}

		return (
			<Styled.CategoriesSection>
				<Styled.CategoriesTitle>Seat Categories</Styled.CategoriesTitle>
				<Styled.LoadingState>No categories available for this seating chart.</Styled.LoadingState>
			</Styled.CategoriesSection>
		);
	}

	return (
		<Styled.CategoriesSection>
			<Styled.CategoriesTitle>Seat Categories</Styled.CategoriesTitle>
			<Styled.CategoriesContainer>
				<Styled.CategoriesList>
					{seatsEvent.categories.map((category) => (
						<Styled.CategoryItem
							key={category.key}
							selected={selectedCategory === String(category.key)}
							onClick={() => onCategorySelect?.(category)}
						>
							<Styled.CategoryColor color={category.color} />
							<Styled.CategoryInfo>
								<Styled.CategoryLabel>{category.label}</Styled.CategoryLabel>
								<Styled.CategoryPlaces>{getPlaceCount(category.label)} places</Styled.CategoryPlaces>
							</Styled.CategoryInfo>
						</Styled.CategoryItem>
					))}
				</Styled.CategoriesList>
				<Styled.ChartContainer>
					{chartDetails ? (
						<Styled.ChartContent>
							<Styled.ChartTitle>{chartDetails.name}</Styled.ChartTitle>
							{chartDetails.publishedVersionThumbnailUrl && (
								<Styled.ChartThumbnail
									src={chartDetails.publishedVersionThumbnailUrl}
									alt={`${chartDetails.name} seating chart`}
								/>
							)}
							<Styled.ChartInfo>Venue: {chartDetails.venueType || 'Standard'}</Styled.ChartInfo>
						</Styled.ChartContent>
					) : (
						<Styled.ChartPlaceholder>
							{chartKey ? 'Loading chart...' : 'No seating chart available'}
						</Styled.ChartPlaceholder>
					)}
				</Styled.ChartContainer>
			</Styled.CategoriesContainer>
		</Styled.CategoriesSection>
	);
};
